// src/repositories/supabase/SupabaseInvoicePaymentRepository.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import type { InvoicePayment, InvoicePaymentMatchMethod, ArApKPIs } from '../../types';
import type { IInvoicePaymentRepository, OverdueInvoiceRow } from '../types';
import { requireAuth } from '../../services/supabaseClient';

const TABLE = 'invoice_payments';

const COLS = [
  'id', 'firm_id', 'company_id',
  'invoice_tx_db_id', 'bank_tx_db_id',
  'monto_aplicado', 'ajuste_redondeo', 'fecha_aplicacion',
  'match_method', 'match_score',
  'journal_entry_cuo', 'ajuste_journal_cuo',
  'created_at', 'created_by',
].join(', ');

function mapRow(row: Record<string, unknown>): InvoicePayment {
  return {
    id:               row.id as string,
    firmId:           row.firm_id as string,
    companyId:        row.company_id as string,
    invoiceTxDbId:    Number(row.invoice_tx_db_id),
    bankTxDbId:       row.bank_tx_db_id != null ? Number(row.bank_tx_db_id) : null,
    montoAplicado:    Number(row.monto_aplicado),
    ajusteRedondeo:   Number(row.ajuste_redondeo ?? 0),
    fechaAplicacion:  row.fecha_aplicacion as string,
    matchMethod:      (row.match_method as InvoicePaymentMatchMethod) ?? 'manual',
    matchScore:       row.match_score != null ? Number(row.match_score) : null,
    journalEntryCuo:  (row.journal_entry_cuo as string | null) ?? null,
    ajusteJournalCuo: (row.ajuste_journal_cuo as string | null) ?? null,
    createdAt:        row.created_at as string,
    createdBy:        (row.created_by as string | null) ?? null,
  };
}

function toInsertRow(payment: Omit<InvoicePayment, 'id' | 'createdAt'>): Record<string, unknown> {
  return {
    firm_id:           payment.firmId,
    company_id:        payment.companyId,
    invoice_tx_db_id:  payment.invoiceTxDbId,
    bank_tx_db_id:     payment.bankTxDbId ?? null,
    monto_aplicado:    payment.montoAplicado,
    ajuste_redondeo:   payment.ajusteRedondeo,
    fecha_aplicacion:  payment.fechaAplicacion,
    match_method:      payment.matchMethod,
    match_score:       payment.matchScore ?? null,
    journal_entry_cuo: payment.journalEntryCuo ?? null,
    ajuste_journal_cuo:payment.ajusteJournalCuo ?? null,
    created_by:        payment.createdBy ?? null,
  };
}

/** Tolerancia para considerar saldo > 0 (evita arrastres por redondeo). */
const SALDO_PENDIENTE_TOL = 0.10;

export class SupabaseInvoicePaymentRepository implements IInvoicePaymentRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  /** Todos los pagos aplicados a una factura (para timeline en CxP/CxC). */
  async getByInvoice(firmId: string, invoiceTxDbId: number): Promise<InvoicePayment[]> {
    await requireAuth();
    const { data, error } = await this.client
      .from(TABLE)
      .select(COLS)
      .eq('firm_id', firmId)
      .eq('invoice_tx_db_id', invoiceTxDbId)
      .order('fecha_aplicacion', { ascending: true });
    if (error) throw new Error(`[getByInvoice] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRow);
  }

  /** Facturas cubiertas por un banco tx (para confirmación en BankInbox). */
  async getByBankTx(firmId: string, bankTxDbId: number): Promise<InvoicePayment[]> {
    await requireAuth();
    const { data, error } = await this.client
      .from(TABLE)
      .select(COLS)
      .eq('firm_id', firmId)
      .eq('bank_tx_db_id', bankTxDbId)
      .order('fecha_aplicacion', { ascending: true });
    if (error) throw new Error(`[getByBankTx] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRow);
  }

  /**
   * Registra un nuevo vínculo factura ↔ banco tx.
   * El `id` y `created_at` los asigna Supabase.
   */
  async create(payment: Omit<InvoicePayment, 'id' | 'createdAt'>): Promise<InvoicePayment> {
    await requireAuth();
    const { data, error } = await this.client
      .from(TABLE)
      .insert(toInsertRow(payment))
      .select(COLS)
      .single();
    if (error) throw new Error(`[create] ${error.message}`);
    return mapRow(data as unknown as Record<string, unknown>);
  }

  /**
   * Actualiza campos de un vínculo existente (e.g. journal_entry_cuo tras generar el JE).
   */
  async update(
    id: string,
    firmId: string,
    fields: Partial<Pick<InvoicePayment,
      'journalEntryCuo' | 'ajusteJournalCuo' | 'matchScore' | 'matchMethod'
    >>,
  ): Promise<void> {
    await requireAuth();
    const row: Record<string, unknown> = {};
    if (fields.journalEntryCuo  !== undefined) row.journal_entry_cuo  = fields.journalEntryCuo;
    if (fields.ajusteJournalCuo !== undefined) row.ajuste_journal_cuo = fields.ajusteJournalCuo;
    if (fields.matchScore       !== undefined) row.match_score        = fields.matchScore;
    if (fields.matchMethod      !== undefined) row.match_method       = fields.matchMethod;

    const { error } = await this.client
      .from(TABLE)
      .update(row)
      .eq('id', id)
      .eq('firm_id', firmId);
    if (error) throw new Error(`[update] ${error.message}`);
  }

  /**
   * KPIs globales AR/AP — llama al RPC `quipus_arap_kpis`.
   *
   * @param fechaRef  Fecha de corte ISO 'YYYY-MM-DD'. Si se omite el RPC usa CURRENT_DATE.
   */
  async getARAPKpis(
    firmId: string,
    companyId: string,
    fechaRef?: string,
  ): Promise<ArApKPIs> {
    await requireAuth();
    // firmId se valida dentro del RPC vía auth.uid() — se pasa solo para consistencia
    void firmId;

    const params: Record<string, unknown> = { p_company_id: companyId };
    if (fechaRef) params.p_fecha_ref = fechaRef;

    const { data, error } = await this.client.rpc('quipus_arap_kpis', params);
    if (error) throw new Error(`[getARAPKpis] ${error.message}`);

    const raw = data as Record<string, unknown>;
    return {
      totalCxP:             Number(raw.totalCxP   ?? 0),
      vencidoCxP:           Number(raw.vencidoCxP ?? 0),
      proximoCxP7d:         Number(raw.proximoCxP7d ?? 0),
      proveedoresActivos:   Number(raw.proveedoresActivos ?? 0),
      totalCxC:             Number(raw.totalCxC   ?? 0),
      vencidoCxC:           Number(raw.vencidoCxC ?? 0),
      proximoCxC7d:         Number(raw.proximoCxC7d ?? 0),
      clientesActivos:      Number(raw.clientesActivos ?? 0),
      proximosVencimientos: Array.isArray(raw.proximosVencimientos)
        ? (raw.proximosVencimientos as Record<string, unknown>[]).map(v => ({
            tipo:             v.tipo as 'cxp' | 'cxc',
            fechaVencimiento: v.fecha_vencimiento as string,
            ruc:              (v.ruc as string | null) ?? null,
            razonSocial:      (v.razon_social as string | null) ?? null,
            saldoPendiente:   Number(v.saldo_pendiente ?? 0),
            comprobante:      (v.comprobante as string) ?? '',
            diasRestantes:    Number(v.dias_restantes ?? 0),
          }))
        : [],
    };
  }

  /**
   * Lista facturas con saldo pendiente cuya fecha de vencimiento ya pasó.
   *
   * Implementación: 2 queries con merge en memoria
   *   1. `invoice_payment_status` view → estado, saldo, dias_vencido
   *   2. `transactions in (db_id list)` → monto signed, tipo_documento,
   *      serie, correlativo, ruc, nombre cliente
   *
   * El view ya filtra `tipo_documento IS NOT NULL AND is_deleted IS NOT TRUE`
   * → solo facturas formales entran. La tolerancia de saldo (0.10) evita
   * que ajustes de redondeo aparezcan como "vencidos".
   */
  async getOverdueInvoices(
    firmId:    string,
    companyId: string,
    fechaRef?: string,
  ): Promise<OverdueInvoiceRow[]> {
    await requireAuth();
    const ref = fechaRef ?? new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    // 1) Status + aging desde la vista
    const { data: statusData, error: statusErr } = await this.client
      .from('invoice_payment_status')
      .select(
        'invoice_db_id, firm_id, company_id, ' +
        'monto_bruto, monto_neto, monto_pagado, saldo_pendiente, ' +
        'payment_status, fecha_vencimiento, dias_vencido'
      )
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .lt('fecha_vencimiento', ref)
      .gt('saldo_pendiente', SALDO_PENDIENTE_TOL)
      .in('payment_status', ['pendiente', 'parcial']);

    if (statusErr) throw new Error(`[getOverdueInvoices/status] ${statusErr.message}`);
    const statusRows = (statusData ?? []) as unknown as Record<string, unknown>[];
    if (statusRows.length === 0) return [];

    // 2) Datos de comprobante desde transactions
    const dbIds = statusRows.map((r) => Number(r.invoice_db_id));
    const { data: txData, error: txErr } = await this.client
      .from('transactions')
      .select(
        'db_id, monto, fecha, tipo_documento, serie, correlativo, ' +
        'ruc_proveedor, ruc_cliente, nombre_cliente'
      )
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .in('db_id', dbIds);

    if (txErr) throw new Error(`[getOverdueInvoices/tx] ${txErr.message}`);

    const byDbId = new Map<number, Record<string, unknown>>();
    for (const t of (txData ?? []) as unknown as Record<string, unknown>[]) {
      byDbId.set(Number(t.db_id), t);
    }

    // Merge — descarta filas del view sin contraparte en transactions
    // (no debería pasar si la integridad referencial es buena)
    return statusRows
      .map((s): OverdueInvoiceRow | null => {
        const dbId = Number(s.invoice_db_id);
        const tx   = byDbId.get(dbId);
        if (!tx) return null;
        return {
          invoiceDbId:      dbId,
          firmId:           s.firm_id as string,
          companyId:        s.company_id as string,
          saldoPendiente:   Number(s.saldo_pendiente),
          montoBruto:       Number(s.monto_bruto),
          montoNeto:        Number(s.monto_neto),
          montoPagado:      Number(s.monto_pagado),
          paymentStatus:    s.payment_status as 'pendiente' | 'parcial' | 'pagado',
          fechaVencimiento: s.fecha_vencimiento as string,
          diasVencido:      Number(s.dias_vencido),
          monto:            Number(tx.monto),
          fecha:            tx.fecha as string,
          tipoDocumento:    (tx.tipo_documento as string) ?? '',
          serie:            (tx.serie as string | null) ?? undefined,
          correlativo:      (tx.correlativo as string | null) ?? undefined,
          rucProveedor:     (tx.ruc_proveedor as string | null) ?? undefined,
          rucCliente:       (tx.ruc_cliente as string | null) ?? undefined,
          nombreCliente:    (tx.nombre_cliente as string | null) ?? undefined,
        };
      })
      .filter((r): r is OverdueInvoiceRow => r !== null);
  }
}
